import { useCallback } from 'react';

import { Copy07Icon } from '@/components/icons/Copy07Icon';
import { Pin01Icon } from '@/components/icons/Pin01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { DeleteMenuItemWithConfirmationModal } from '@/components/poppers/MenuPopper/DeleteMenuItemWithConfirmationModal';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { shouldRefetchQuery } from '@/graphql/client.utils';
import { useReportError } from '@/hooks/useReportError';
import { PresentationTemplateType } from '@/types/schema';

import { Operations as SystemPresentationTemplateTableOperations } from '../systemPresentationTemplateTable/graphql/SystemPresentationTemplateTable.generated';
import { UserPresentationTemplateTableDocument } from '../userPresentationTemplateTable/graphql/UserPresentationTemplateTable.generated';
import {
  Operations as TenantPresentationTemplateTableOperations,
  TenantPresentationTemplateTableDocument,
  useDeletePresentationTemplateMutation,
  useDuplicatePresentationTemplateMutation,
  useUpdatePresentationTemplatePinMutation,
} from './graphql/TenantPresentationTemplateTable.generated';

// The firm- and user-level template tables aren't always both mounted (the
// admin page has no user table), so refetching them by name would log
// "Unknown query" warnings for whichever table isn't active. Refetch only the
// currently-active tables among these instead.
const REFETCH_QUERY_DOCUMENTS = [
  TenantPresentationTemplateTableDocument,
  UserPresentationTemplateTableDocument,
];

interface PresentationTemplateMenuItemProps {
  presentationTemplateId: string;
  onAction: () => void;
}

interface DuplicatePresentationTemplateMenuItemProps {
  presentationTemplateId: string;
  label?: string;
  // The template level the duplicate should be created at, based on where the
  // table is rendered (firm-level admin vs. user-level household page).
  duplicateType: PresentationTemplateType;
  // Called with the newly-created template's id once duplication succeeds. When
  // provided (system-template tables), the caller opens the duplicate in the
  // editor; otherwise the user stays on the list, which refreshes in place.
  onDuplicateComplete?: (newTemplateId: string) => void;
}

interface UpdatePresentationTemplatePinMenuItemProps {
  isPinned: boolean;
  presentationTemplateId: string;
}

export function UpdatePresentationTemplatePinMenuItem({
  isPinned,
  presentationTemplateId,
}: UpdatePresentationTemplatePinMenuItemProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [updatePresentationTemplatePin, { loading }] =
    useUpdatePresentationTemplatePinMutation({
      refetchQueries: [
        TenantPresentationTemplateTableOperations.Query
          .TenantPresentationTemplateTable,
        SystemPresentationTemplateTableOperations.Query
          .SystemPresentationTemplateTable,
      ],
      awaitRefetchQueries: true,
      onError: (error) => {
        showFeedback(
          `Failed to ${isPinned ? 'unpin' : 'pin'} the presentation template`
        );
        reportError(
          `Failed to ${isPinned ? 'unpin' : 'pin'} the presentation template`,
          error
        );
      },
    });

  const onClick = useCallback(() => {
    void updatePresentationTemplatePin({
      variables: {
        id: presentationTemplateId,
        isPinned: !isPinned,
      },
    });
  }, [isPinned, presentationTemplateId, updatePresentationTemplatePin]);

  return (
    <MenuItem
      icon={<Pin01Icon size={16} />}
      label={
        isPinned ? 'Unpin from client onboarding' : 'Pin to client onboarding'
      }
      loading={loading}
      onClick={onClick}
    />
  );
}

export function DuplicatePresentationTemplateMenuItem({
  presentationTemplateId,
  duplicateType,
  onDuplicateComplete,
  label = 'Duplicate',
}: DuplicatePresentationTemplateMenuItemProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [duplicatePresentationTemplate, { loading }] =
    useDuplicatePresentationTemplateMutation({
      // Refresh both the firm- and user-level tables so the duplicate is
      // present when the user navigates back from the editor. The active
      // queries refetch with their existing variables.
      refetchQueries: 'active',
      onQueryUpdated: (query) =>
        shouldRefetchQuery(query.queryName, {
          allowedQueryDocuments: REFETCH_QUERY_DOCUMENTS,
        }),
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        const duplicateTemplateId = data.duplicatePresentationTemplate?.id;
        if (duplicateTemplateId) {
          showFeedback('Presentation template duplicated successfully', {
            variant: 'success',
          });
          onDuplicateComplete?.(duplicateTemplateId);
        } else {
          showFeedback('Failed to duplicate presentation template');
          reportError('Failed to duplicate presentation template');
        }
      },
      onError: (error) => {
        showFeedback('Failed to duplicate presentation template');
        reportError('Failed to duplicate presentation template', error);
      },
    });

  const onClick = useCallback(() => {
    duplicatePresentationTemplate({
      variables: {
        id: presentationTemplateId,
        type: duplicateType,
      },
    });
  }, [duplicatePresentationTemplate, presentationTemplateId, duplicateType]);

  return (
    <MenuItem
      icon={<Copy07Icon size={16} />}
      label={label}
      onClick={onClick}
      loading={loading}
    />
  );
}

export function DeletePresentationTemplateMenuItem({
  presentationTemplateId,
  onAction,
}: PresentationTemplateMenuItemProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [deletePresentationTemplate, { loading }] =
    useDeletePresentationTemplateMutation({
      onCompleted: () => {
        onAction();
        showFeedback('Presentation template deleted successfully', {
          variant: 'success',
        });
      },
      onError: (error) => {
        showFeedback('Failed to delete presentation template');
        reportError('Failed to delete presentation template', error);
      },
    });

  const onClick = useCallback(() => {
    deletePresentationTemplate({
      variables: {
        id: presentationTemplateId,
      },
    });
  }, [deletePresentationTemplate, presentationTemplateId]);

  return (
    <DeleteMenuItemWithConfirmationModal
      label="Delete"
      onConfirmDelete={onClick}
      menuItemProps={{ loading }}
    />
  );
}
